import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'components/common/Modal';
import CompanyAutocomplete from 'components/common/CompanyAutocomplete';
import bankCredentialService from '../../services/bankCredentialService';
import api from 'services/api';
import { Save, Loader2, Eye, EyeOff } from 'lucide-react';

const ModalBankCredential = ({ isOpen, onClose, onSuccess, credential, banks = [] }) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    company_id: '',
    id_conta_bancaria: '',
    account_alias_ids_text: '',
    bank_id: '',
    is_active: true,
    base_url_api: '',
    base_url_auth: '',
    pfx_base64: '',
    pfx_passphrase: '',
    tls_type: 'pfx',
    auth_mode: 'shared',
    auth_token_url: '',
    auth_grant_type: 'client_credentials',
    auth_client_id: '',
    auth_client_secret: '',
    auth_token_url_pix: '',
    auth_client_id_pix: '',
    auth_client_secret_pix: '',
    auth_token_url_boleto: '',
    auth_client_id_boleto: '',
    auth_client_secret_boleto: '',
    bradesco_banco_debito: '',
    bradesco_agencia_debito: '',
    bradesco_digito_agencia_debito: '',
    bradesco_conta_debito: '',
    bradesco_digito_conta_debito: '',
    timeout_pay: 600000,
    execution_retry: false,
    execution_single_call: true,
    execution_allow_polling: false,
  });

  const [showPfxPassphrase, setShowPfxPassphrase] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (credential) {
        // Edição: carregar dados existentes
        const config = credential.credentials_json || {};
        const authConfig = config.auth || {};
        const hasContextAuth = ['default', 'pix', 'boleto'].some(
          (k) => authConfig[k] && typeof authConfig[k] === 'object'
        );
        const authDefault = hasContextAuth ? (authConfig.default || {}) : authConfig;
        const authPix = hasContextAuth ? (authConfig.pix || {}) : {};
        const authBoleto = hasContextAuth ? (authConfig.boleto || {}) : {};
        const bradescoConfig = config.bradesco || {};
        const bradescoDebit = bradescoConfig.debit || {};
        
        // Carregar empresa completa se necessário
        if (credential.company_id && credential.company) {
          setSelectedCompany({
            id: credential.company.id,
            name: credential.company.name,
          });
        } else if (credential.company_id) {
          // Buscar empresa se não veio no relacionamento
          api.get(`/companies/${credential.company_id}`)
            .then((response) => {
              const company = response.data.data;
              setSelectedCompany({
                id: company.id,
                name: company.name,
              });
            })
            .catch(() => {
              setSelectedCompany({ id: credential.company_id, name: '' });
            });
        }
        
        setForm({
          company_id: credential.company_id,
          id_conta_bancaria: credential.id_conta_bancaria || '',
          account_alias_ids_text: Array.isArray(config.account_alias_ids) ? config.account_alias_ids.join(', ') : '',
          bank_id: credential.bank_id,
          is_active: credential.is_active,
          base_url_api: config.base_urls?.api || '',
          base_url_auth: config.base_urls?.auth || '',
          pfx_base64: '',
          pfx_passphrase: '',
          tls_type: config.tls?.type || 'pfx',
          auth_mode: hasContextAuth ? 'separate' : 'shared',
          auth_token_url: authDefault?.token_url || '',
          auth_grant_type: authDefault?.grant_type || 'client_credentials',
          auth_client_id: authDefault?.client_id || '',
          auth_client_secret: authDefault?.secret || '',
          auth_token_url_pix: authPix?.token_url || '',
          auth_client_id_pix: authPix?.client_id || '',
          auth_client_secret_pix: authPix?.secret || '',
          auth_token_url_boleto: authBoleto?.token_url || '',
          auth_client_id_boleto: authBoleto?.client_id || '',
          auth_client_secret_boleto: authBoleto?.secret || '',
          bradesco_banco_debito: bradescoDebit?.bank_code || '',
          bradesco_agencia_debito: bradescoDebit?.branch || '',
          bradesco_digito_agencia_debito: bradescoDebit?.branch_digit || '',
          bradesco_conta_debito: bradescoDebit?.account || '',
          bradesco_digito_conta_debito: bradescoDebit?.account_digit || '',
          timeout_pay: config.timeouts?.pay || 600000,
          execution_retry: config.execution?.retry || false,
          execution_single_call: config.execution?.single_call !== false,
          execution_allow_polling: config.execution?.allow_polling || false,
        });
      } else {
        // Criação: resetar formulário
        setForm({
          company_id: '',
          id_conta_bancaria: '',
          account_alias_ids_text: '',
          bank_id: '',
          is_active: true,
          base_url_api: '',
          base_url_auth: '',
          pfx_base64: '',
          pfx_passphrase: '',
          tls_type: 'pfx',
          auth_mode: 'shared',
          auth_token_url: '',
          auth_grant_type: 'client_credentials',
          auth_client_id: '',
          auth_client_secret: '',
          auth_token_url_pix: '',
          auth_client_id_pix: '',
          auth_client_secret_pix: '',
          auth_token_url_boleto: '',
          auth_client_id_boleto: '',
          auth_client_secret_boleto: '',
          bradesco_banco_debito: '',
          bradesco_agencia_debito: '',
          bradesco_digito_agencia_debito: '',
          bradesco_conta_debito: '',
          bradesco_digito_conta_debito: '',
          timeout_pay: 600000,
          execution_retry: false,
          execution_single_call: true,
          execution_allow_polling: false,
        });
        setSelectedCompany(null);
        setBankAccounts([]);
      }
      setErrors({});
    }
  }, [isOpen, credential]);

  // Função para carregar contas bancárias
  const loadBankAccounts = useCallback(async (companyId, preserveIdContaBancaria = null) => {
    if (!companyId) return;
    
    setLoadingAccounts(true);
    try {
      console.log('Carregando contas bancárias para company_id:', companyId, 'preserveId:', preserveIdContaBancaria);
      const response = await api.get(`/admin/bank-credentials/companies/${companyId}/bank-accounts`);
      const contas = response.data.data || [];
      console.log('Contas bancárias carregadas:', contas);
      setBankAccounts(contas);
      
      // Se houver apenas uma conta, selecionar automaticamente (apenas em criação)
      if (contas.length === 1 && !preserveIdContaBancaria) {
        const conta = contas[0];
        setForm(prev => ({
          ...prev,
          id_conta_bancaria: conta.id_conta_bancaria,
          bank_id: conta.bank_id || prev.bank_id,
        }));
      } else if (preserveIdContaBancaria) {
        // Em modo de edição, preservar o id_conta_bancaria existente
        const contaEncontrada = contas.find(c => c.id_conta_bancaria === preserveIdContaBancaria);
        setForm(prev => ({
          ...prev,
          id_conta_bancaria: preserveIdContaBancaria,
          bank_id: contaEncontrada?.bank_id || prev.bank_id,
        }));
        console.log('Preservando id_conta_bancaria:', preserveIdContaBancaria, 'conta encontrada:', contaEncontrada);
      }
    } catch (error) {
      console.error('Erro ao carregar contas bancárias:', error);
      setBankAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  // Carregar contas bancárias quando empresa é selecionada (criação e edição)
  useEffect(() => {
    console.log('useEffect contas bancárias:', { 
      isOpen, 
      hasCredential: !!credential, 
      company_id: form.company_id,
      id_conta_bancaria: form.id_conta_bancaria
    });
    
    if (isOpen && form.company_id) {
      console.log('Disparando loadBankAccounts para company_id:', form.company_id);
      // Se estiver em modo de edição e já tiver id_conta_bancaria, preservar
      const preserveId = credential && form.id_conta_bancaria ? form.id_conta_bancaria : null;
      loadBankAccounts(form.company_id, preserveId);
    } else if (isOpen && !form.company_id) {
      setBankAccounts([]);
      setForm(prev => ({ ...prev, id_conta_bancaria: '', bank_id: '' }));
    }
  }, [form.company_id, form.id_conta_bancaria, isOpen, loadBankAccounts, credential]);

  // Carregar contas quando o modal abre em modo de edição
  useEffect(() => {
    if (isOpen && credential && credential.company_id && credential.id_conta_bancaria) {
      console.log('Modal aberto em edição, carregando contas bancárias');
      // Aguardar um pouco para garantir que o form foi atualizado pelo primeiro useEffect
      const timer = setTimeout(() => {
        // Carregar contas preservando o id_conta_bancaria existente
        if (bankAccounts.length === 0) {
          console.log('Carregando contas bancárias para edição, company_id:', credential.company_id, 'id_conta_bancaria:', credential.id_conta_bancaria);
          loadBankAccounts(credential.company_id, credential.id_conta_bancaria);
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, credential, credential?.company_id, credential?.id_conta_bancaria, bankAccounts.length, loadBankAccounts]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    try {
      const data = { ...form };
      const aliasText = String(data.account_alias_ids_text || '').trim();
      const parsedAliases = aliasText
        ? aliasText
            .split(',')
            .map((v) => parseInt(v.trim(), 10))
            .filter((v) => Number.isInteger(v) && v > 0)
        : [];
      data.account_alias_ids = Array.from(new Set(parsedAliases)).filter(
        (v) => v !== parseInt(data.id_conta_bancaria, 10)
      );
      delete data.account_alias_ids_text;
      if (data.auth_mode === 'shared') {
        data.auth_token_url_pix = '';
        data.auth_client_id_pix = '';
        data.auth_client_secret_pix = '';
        data.auth_token_url_boleto = '';
        data.auth_client_id_boleto = '';
        data.auth_client_secret_boleto = '';
      }

      if (data.bank_id !== 'bradesco') {
        data.bradesco_banco_debito = '';
        data.bradesco_agencia_debito = '';
        data.bradesco_digito_agencia_debito = '';
        data.bradesco_conta_debito = '';
        data.bradesco_digito_conta_debito = '';
      }

      if (credential) {
        await bankCredentialService.update(credential.id, data);
      } else {
        await bankCredentialService.create(data);
      }

      onSuccess();
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'Erro ao salvar credencial' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={credential ? 'Editar Credencial Bancária' : 'Nova Credencial Bancária'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="p-6">
        {errors.general && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            {errors.general}
          </div>
        )}

        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
          {/* Dados Básicos */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados Básicos</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa <span className="text-red-500">*</span>
                </label>
                <CompanyAutocomplete
                  value={selectedCompany}
                  onChange={(company) => {
                    setSelectedCompany(company);
                    setForm((prev) => ({ ...prev, company_id: company?.id || '' }));
                  }}
                  error={errors.company_id}
                />
                {errors.company_id && (
                  <p className="mt-1 text-sm text-red-600">
                    {Array.isArray(errors.company_id) ? errors.company_id[0] : errors.company_id}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conta Bancária <span className="text-red-500">*</span>
                </label>
                {loadingAccounts ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Carregando contas bancárias...
                  </div>
                ) : (
                  <select
                    value={String(form.id_conta_bancaria || '')}
                    onChange={(e) => {
                      const contaSelecionada = bankAccounts.find(c => c.id_conta_bancaria === e.target.value);
                      setForm(prev => ({
                        ...prev,
                        id_conta_bancaria: e.target.value ? parseInt(e.target.value) : '',
                        bank_id: contaSelecionada?.bank_id || prev.bank_id,
                      }));
                    }}
                    className={`w-full border rounded-lg px-3 py-2 ${errors.id_conta_bancaria ? 'border-red-300' : 'border-[color:var(--border)]'} ${credential ? 'bg-[color:var(--surface-muted)]' : 'bg-[color:var(--surface)]'} text-[color:var(--text-primary)]`}
                    required
                    disabled={!form.company_id}
                    style={{
                      backgroundColor: credential ? 'var(--surface-muted)' : 'var(--surface)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="" style={{ backgroundColor: 'var(--surface)', color: 'var(--text-primary)' }}>
                      Selecione uma conta bancária
                    </option>
                    {bankAccounts.map((conta) => (
                      <option
                        key={conta.id}
                        value={String(conta.id_conta_bancaria)}
                        style={{ backgroundColor: 'var(--surface)', color: 'var(--text-primary)' }}
                      >
                        {conta.nome} - {conta.banco_nome || conta.banco_codigo || 'Banco'} {conta.agencia ? `(${conta.agencia})` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {errors.id_conta_bancaria && (
                  <p className="mt-1 text-sm text-red-600">
                    {Array.isArray(errors.id_conta_bancaria) ? errors.id_conta_bancaria[0] : errors.id_conta_bancaria}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Selecione a conta bancária específica que será usada para esta credencial
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IDs de Conta Bancária (Aliases)
                </label>
                <input
                  type="text"
                  value={form.account_alias_ids_text}
                  onChange={(e) => setForm((prev) => ({ ...prev, account_alias_ids_text: e.target.value }))}
                  placeholder="Ex: 10, 12, 15"
                  className={`w-full border rounded-lg px-3 py-2 ${errors.account_alias_ids ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.account_alias_ids && (
                  <p className="mt-1 text-sm text-red-600">
                    {Array.isArray(errors.account_alias_ids) ? errors.account_alias_ids[0] : errors.account_alias_ids}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Opcional. Use quando a mesma conta real possui IDs diferentes por unidade.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Banco <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.bank_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, bank_id: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 ${errors.bank_id ? 'border-red-300' : 'border-[color:var(--border)]'} bg-[color:var(--surface)] text-[color:var(--text-primary)]`}
                  required
                  style={{ backgroundColor: 'var(--surface)', color: 'var(--text-primary)' }}
                >
                  <option value="" style={{ backgroundColor: 'var(--surface)', color: 'var(--text-primary)' }}>
                    Selecione um banco
                  </option>
                  {banks.map((bank) => (
                    <option
                      key={bank.id}
                      value={bank.id}
                      style={{ backgroundColor: 'var(--surface)', color: 'var(--text-primary)' }}
                    >
                      {bank.name}
                    </option>
                  ))}
                </select>
                {errors.bank_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.bank_id[0]}</p>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Ativo</span>
                </label>
              </div>
            </div>
          </section>

          {/* Base URLs */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Base URLs</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Base URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={form.base_url_api}
                  onChange={(e) => setForm((prev) => ({ ...prev, base_url_api: e.target.value }))}
                  placeholder="https://api.banco.com"
                  className={`w-full border rounded-lg px-3 py-2 ${errors.base_url_api ? 'border-red-300' : 'border-gray-300'}`}
                  required
                />
                {errors.base_url_api && (
                  <p className="mt-1 text-sm text-red-600">
                    {Array.isArray(errors.base_url_api) ? errors.base_url_api[0] : errors.base_url_api}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  URL base da API do banco (deve usar HTTPS)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Auth Base URL
                </label>
                <input
                  type="url"
                  value={form.base_url_auth}
                  onChange={(e) => setForm((prev) => ({ ...prev, base_url_auth: e.target.value }))}
                  placeholder="https://auth.banco.com"
                  className={`w-full border rounded-lg px-3 py-2 ${errors.base_url_auth ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.base_url_auth && (
                  <p className="mt-1 text-sm text-red-600">
                    {Array.isArray(errors.base_url_auth) ? errors.base_url_auth[0] : errors.base_url_auth}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  URL base para autenticação (opcional, usa API Base URL se não informado)
                </p>
              </div>
            </div>
          </section>

          {form.bank_id === 'bradesco' && (
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados de Débito Bradesco</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Banco Débito <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.bradesco_banco_debito}
                    onChange={(e) => setForm((prev) => ({ ...prev, bradesco_banco_debito: e.target.value }))}
                    placeholder="237"
                    className={`w-full border rounded-lg px-3 py-2 ${errors.bradesco_banco_debito ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {errors.bradesco_banco_debito && (
                    <p className="mt-1 text-sm text-red-600">
                      {Array.isArray(errors.bradesco_banco_debito) ? errors.bradesco_banco_debito[0] : errors.bradesco_banco_debito}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agência Débito <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.bradesco_agencia_debito}
                    onChange={(e) => setForm((prev) => ({ ...prev, bradesco_agencia_debito: e.target.value }))}
                    placeholder="1234"
                    className={`w-full border rounded-lg px-3 py-2 ${errors.bradesco_agencia_debito ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {errors.bradesco_agencia_debito && (
                    <p className="mt-1 text-sm text-red-600">
                      {Array.isArray(errors.bradesco_agencia_debito) ? errors.bradesco_agencia_debito[0] : errors.bradesco_agencia_debito}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dígito Agência <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.bradesco_digito_agencia_debito}
                    onChange={(e) => setForm((prev) => ({ ...prev, bradesco_digito_agencia_debito: e.target.value }))}
                    placeholder="0"
                    className={`w-full border rounded-lg px-3 py-2 ${errors.bradesco_digito_agencia_debito ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {errors.bradesco_digito_agencia_debito && (
                    <p className="mt-1 text-sm text-red-600">
                      {Array.isArray(errors.bradesco_digito_agencia_debito) ? errors.bradesco_digito_agencia_debito[0] : errors.bradesco_digito_agencia_debito}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conta Débito <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.bradesco_conta_debito}
                    onChange={(e) => setForm((prev) => ({ ...prev, bradesco_conta_debito: e.target.value }))}
                    placeholder="1234567"
                    className={`w-full border rounded-lg px-3 py-2 ${errors.bradesco_conta_debito ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {errors.bradesco_conta_debito && (
                    <p className="mt-1 text-sm text-red-600">
                      {Array.isArray(errors.bradesco_conta_debito) ? errors.bradesco_conta_debito[0] : errors.bradesco_conta_debito}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dígito Conta <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.bradesco_digito_conta_debito}
                    onChange={(e) => setForm((prev) => ({ ...prev, bradesco_digito_conta_debito: e.target.value }))}
                    placeholder="9"
                    className={`w-full border rounded-lg px-3 py-2 ${errors.bradesco_digito_conta_debito ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {errors.bradesco_digito_conta_debito && (
                    <p className="mt-1 text-sm text-red-600">
                      {Array.isArray(errors.bradesco_digito_conta_debito) ? errors.bradesco_digito_conta_debito[0] : errors.bradesco_digito_conta_debito}
                    </p>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Esses dados são necessários para montar o pagador no fluxo de boletos Bradesco.
              </p>
            </section>
          )}

          {/* Certificados */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Certificado mTLS (PFX/PKCS#12)</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Arquivo PFX/P12 <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept=".pfx,.p12"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    // Validar extensão
                    const ext = file.name.split('.').pop()?.toLowerCase();
                    if (ext !== 'pfx' && ext !== 'p12') {
                      setErrors((prev) => ({ ...prev, pfx_base64: 'Arquivo deve ser .pfx ou .p12' }));
                      return;
                    }
                    
                    // Validar tamanho (máximo 5MB)
                    if (file.size > 5 * 1024 * 1024) {
                      setErrors((prev) => ({ ...prev, pfx_base64: 'Arquivo muito grande (máximo 5MB)' }));
                      return;
                    }
                    
                    try {
                      // Converter para base64
                      const base64 = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                          const result = reader.result;
                          // Remover prefixo data:application/...;base64,
                          const base64Data = result.includes(',') ? result.split(',')[1] : result;
                          resolve(base64Data);
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(file);
                      });
                      
                      setForm((prev) => ({ ...prev, pfx_base64: base64 }));
                      setErrors((prev) => ({ ...prev, pfx_base64: null }));
                    } catch (error) {
                      setErrors((prev) => ({ ...prev, pfx_base64: 'Erro ao processar arquivo' }));
                    }
                  }}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${errors.pfx_base64 ? 'border-red-300' : 'border-gray-300'}`}
                  required
                />
                {errors.pfx_base64 && (
                  <p className="mt-1 text-sm text-red-600">
                    {Array.isArray(errors.pfx_base64) ? errors.pfx_base64[0] : errors.pfx_base64}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Selecione o arquivo de certificado PFX/PKCS#12 fornecido pelo banco.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passphrase do PFX <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPfxPassphrase ? 'text' : 'password'}
                    value={form.pfx_passphrase}
                    onChange={(e) => setForm((prev) => ({ ...prev, pfx_passphrase: e.target.value }))}
                    className={`w-full border rounded-lg px-3 py-2 pr-10 ${errors.pfx_passphrase ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="Digite a passphrase do certificado PFX"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPfxPassphrase(!showPfxPassphrase)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPfxPassphrase ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.pfx_passphrase && (
                  <p className="mt-1 text-sm text-red-600">
                    {Array.isArray(errors.pfx_passphrase) ? errors.pfx_passphrase[0] : errors.pfx_passphrase}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Senha usada para proteger o arquivo PFX.
                </p>
              </div>
            </div>
          </section>

          {/* Autenticação */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Autenticação</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modo de autenticação
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <label className="flex items-start gap-2 border border-gray-200 rounded-lg p-3 cursor-pointer">
                    <input
                      type="radio"
                      name="auth_mode"
                      value="shared"
                      checked={form.auth_mode === 'shared'}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_mode: e.target.value }))}
                      className="mt-1"
                    />
                    <span className="text-sm text-gray-700">
                      Mesma autenticação para PIX e BOLETO
                    </span>
                  </label>
                  <label className="flex items-start gap-2 border border-gray-200 rounded-lg p-3 cursor-pointer">
                    <input
                      type="radio"
                      name="auth_mode"
                      value="separate"
                      checked={form.auth_mode === 'separate'}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_mode: e.target.value }))}
                      className="mt-1"
                    />
                    <span className="text-sm text-gray-700">
                      Autenticação separada por contexto (PIX/BOLETO)
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grant Type
                </label>
                <select
                  value={form.auth_grant_type}
                  onChange={(e) => setForm((prev) => ({ ...prev, auth_grant_type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="client_credentials">Client Credentials</option>
                  <option value="password">Password</option>
                  <option value="authorization_code">Authorization Code</option>
                </select>
              </div>

              {form.auth_mode === 'shared' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Token URL
                    </label>
                    <input
                      type="text"
                      value={form.auth_token_url}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_token_url: e.target.value }))}
                      placeholder="/oauth/token"
                      className={`w-full border rounded-lg px-3 py-2 ${errors.auth_token_url ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    {errors.auth_token_url && (
                      <p className="mt-1 text-sm text-red-600">
                        {Array.isArray(errors.auth_token_url) ? errors.auth_token_url[0] : errors.auth_token_url}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={form.auth_client_id}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_client_id: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2 ${errors.auth_client_id ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    {errors.auth_client_id && (
                      <p className="mt-1 text-sm text-red-600">
                        {Array.isArray(errors.auth_client_id) ? errors.auth_client_id[0] : errors.auth_client_id}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Secret
                    </label>
                    <div className="relative">
                      <input
                        type={showClientSecret ? 'text' : 'password'}
                        value={form.auth_client_secret}
                        onChange={(e) => setForm((prev) => ({ ...prev, auth_client_secret: e.target.value }))}
                        className={`w-full border rounded-lg px-3 py-2 pr-10 ${errors.auth_client_secret ? 'border-red-300' : 'border-gray-300'}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowClientSecret(!showClientSecret)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showClientSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.auth_client_secret && (
                      <p className="mt-1 text-sm text-red-600">
                        {Array.isArray(errors.auth_client_secret) ? errors.auth_client_secret[0] : errors.auth_client_secret}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-800">Autenticação PIX</p>
                    <input
                      type="text"
                      value={form.auth_token_url_pix}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_token_url_pix: e.target.value }))}
                      placeholder="Token URL PIX (ex: /oauth/token/pix)"
                      className={`w-full border rounded-lg px-3 py-2 ${errors.auth_token_url_pix ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    <input
                      type="text"
                      value={form.auth_client_id_pix}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_client_id_pix: e.target.value }))}
                      placeholder="Client ID PIX"
                      className={`w-full border rounded-lg px-3 py-2 ${errors.auth_client_id_pix ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    <input
                      type="password"
                      value={form.auth_client_secret_pix}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_client_secret_pix: e.target.value }))}
                      placeholder="Client Secret PIX"
                      className={`w-full border rounded-lg px-3 py-2 ${errors.auth_client_secret_pix ? 'border-red-300' : 'border-gray-300'}`}
                    />
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-800">Autenticação BOLETO</p>
                    <input
                      type="text"
                      value={form.auth_token_url_boleto}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_token_url_boleto: e.target.value }))}
                      placeholder="Token URL BOLETO (ex: /oauth/token/boleto)"
                      className={`w-full border rounded-lg px-3 py-2 ${errors.auth_token_url_boleto ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    <input
                      type="text"
                      value={form.auth_client_id_boleto}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_client_id_boleto: e.target.value }))}
                      placeholder="Client ID BOLETO"
                      className={`w-full border rounded-lg px-3 py-2 ${errors.auth_client_id_boleto ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    <input
                      type="password"
                      value={form.auth_client_secret_boleto}
                      onChange={(e) => setForm((prev) => ({ ...prev, auth_client_secret_boleto: e.target.value }))}
                      placeholder="Client Secret BOLETO"
                      className={`w-full border rounded-lg px-3 py-2 ${errors.auth_client_secret_boleto ? 'border-red-300' : 'border-gray-300'}`}
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Timeouts */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeouts</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timeout de Pagamento (ms)
              </label>
              <input
                type="number"
                value={form.timeout_pay}
                onChange={(e) => setForm((prev) => ({ ...prev, timeout_pay: parseInt(e.target.value) || 600000 }))}
                min={1000}
                max={3600000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
              <p className="mt-1 text-xs text-gray-500">
                Padrão: 600000ms (10 minutos). Máximo: 3600000ms (1 hora)
              </p>
            </div>
          </section>

          {/* Regras de Execução */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Regras de Execução</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.execution_retry}
                  onChange={(e) => setForm((prev) => ({ ...prev, execution_retry: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Retry automático</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.execution_single_call}
                  onChange={(e) => setForm((prev) => ({ ...prev, execution_single_call: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Execução em chamada única</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.execution_allow_polling}
                  onChange={(e) => setForm((prev) => ({ ...prev, execution_allow_polling: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Polling automático</span>
              </label>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Salvar
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ModalBankCredential;
